import { PubSubService } from '@ohif/core';
import {
  ORGANS,
  OrganEnum,
  OrganQualityEnum,
  OrganReview,
  OrganReviewStatusEnum,
  OrganReviewSubmission,
  fetchOrganReviews,
  submitOrganReviews,
} from '../clients/viewer';
import { UnauthorizedException } from '../http';
import { describeError } from '../utils';

const EVENTS = {
  ORGAN_REVIEWS_CHANGED: 'event::sycai:OrganReviewService:organReviewsChanged',
  SESSION_LOST: 'event::sycai:OrganReviewService:sessionLost',
} as const;

export type OrganReviewEventPayloads = {
  [EVENTS.ORGAN_REVIEWS_CHANGED]: { studyInstanceUid: string; seriesInstanceUid: string };
  [EVENTS.SESSION_LOST]: { studyInstanceUid: string; seriesInstanceUid: string };
};

export type OrganRowState = 'unissued' | 'gradeable' | 'inProgress' | 'complete' | 'failed';

export type OrganRow =
  | { organ: OrganEnum; state: 'unissued' }
  | {
      organ: OrganEnum;
      state: Exclude<OrganRowState, 'unissued'>;
      reviewId: string;
      quality?: OrganQualityEnum;
    };

interface OrganEntry {
  reviewId: string;
  status?: OrganReviewStatusEnum;
  quality?: OrganQualityEnum;
  isLocalQuality: boolean;
  isOrphaned: boolean;
}

type SeriesEntries = Map<OrganEnum, OrganEntry>;

const NO_ENTRIES: SeriesEntries = new Map();

function toRow(organ: OrganEnum, entry: OrganEntry | undefined): OrganRow {
  if (!entry || entry.isOrphaned) {
    return { organ, state: 'unissued' };
  }

  const { reviewId, status, quality } = entry;

  if (status === OrganReviewStatusEnum.IN_PROGRESS) {
    return { organ, state: 'inProgress', reviewId, quality };
  }

  if (status === OrganReviewStatusEnum.COMPLETED) {
    return { organ, state: 'complete', reviewId, quality };
  }

  if (status === OrganReviewStatusEnum.FAILED) {
    return { organ, state: 'failed', reviewId, quality };
  }

  return { organ, state: 'gradeable', reviewId, quality };
}

export function isSubmittable(
  row: OrganRow
): row is Extract<OrganRow, { reviewId: string }> & { quality: OrganQualityEnum } {
  return (row.state === 'gradeable' || row.state === 'failed') && row.quality !== undefined;
}

export class OrganReviewService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'organReviewService',
    create: () => new OrganReviewService(),
  };

  private studies = new Map<string, Map<string, SeriesEntries>>();

  private generations = new Map<string, number>();

  private submitting = new Set<string>();

  private failedReads = new Set<string>();

  private refreshesInFlight = new Map<string, { generation: number; request: Promise<void> }>();

  constructor() {
    super(EVENTS);
  }

  private emit<E extends keyof OrganReviewEventPayloads>(
    event: E,
    payload: OrganReviewEventPayloads[E]
  ): void {
    this._broadcastEvent(event, payload);
  }

  public getRows(studyInstanceUid: string, seriesInstanceUid: string): readonly OrganRow[] {
    const entries = this.entriesOf(studyInstanceUid, seriesInstanceUid);

    return ORGANS.map(organ => toRow(organ, entries.get(organ)));
  }

  public hasError(seriesInstanceUid: string): boolean {
    return this.failedReads.has(seriesInstanceUid);
  }

  public getSubmittable(
    studyInstanceUid: string,
    seriesInstanceUid: string
  ): OrganReviewSubmission[] {
    return this.getRows(studyInstanceUid, seriesInstanceUid)
      .filter(isSubmittable)
      .map(({ reviewId, quality }) => ({ reviewId, quality }));
  }

  public setQuality(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    organ: OrganEnum,
    quality?: OrganQualityEnum
  ): void {
    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);
    const entry = entries.get(organ);

    // Unissued rows are not gradeable, and the panel disables them; this is the guard
    // that makes that a property of the service rather than of the renderer.
    if (!entry || entry.isOrphaned || entry.status === OrganReviewStatusEnum.COMPLETED) {
      return;
    }

    entries.set(organ, { ...entry, quality, isLocalQuality: true });
    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  public async refresh(studyInstanceUid: string, seriesInstanceUid: string): Promise<void> {
    this.ensureGeneration(seriesInstanceUid);
    const generation = this.generationOf(seriesInstanceUid);
    const inFlight = this.refreshesInFlight.get(seriesInstanceUid);

    if (inFlight && inFlight.generation === generation) {
      return inFlight.request;
    }

    const request = this.read(studyInstanceUid, seriesInstanceUid, generation).finally(() => {
      if (this.refreshesInFlight.get(seriesInstanceUid)?.request === request) {
        this.refreshesInFlight.delete(seriesInstanceUid);
      }
    });

    this.refreshesInFlight.set(seriesInstanceUid, { generation, request });

    return request;
  }

  private async read(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    generation: number
  ): Promise<void> {
    try {
      const reviews = await fetchOrganReviews(seriesInstanceUid);

      if (this.isStale(seriesInstanceUid, generation)) {
        return;
      }

      this.failedReads.delete(seriesInstanceUid);
      this.merge(studyInstanceUid, seriesInstanceUid, reviews);
    } catch (error) {
      if (this.isStale(seriesInstanceUid, generation)) {
        return;
      }

      this.failedReads.add(seriesInstanceUid);
      this.report(studyInstanceUid, seriesInstanceUid, 'reading the organ reviews', error);
      this.broadcast(studyInstanceUid, seriesInstanceUid);
    }
  }

  public async submit(studyInstanceUid: string, seriesInstanceUid: string): Promise<void> {
    const submission = this.getSubmittable(studyInstanceUid, seriesInstanceUid);

    if (submission.length === 0) {
      return;
    }

    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);

    const snapshot = new Map(entries);
    const submitted = new Set(submission.map(({ reviewId }) => reviewId));

    this.bumpGeneration(seriesInstanceUid);
    const generation = this.generationOf(seriesInstanceUid);
    this.submitting.add(seriesInstanceUid);

    entries.forEach((entry, organ) => {
      if (submitted.has(entry.reviewId)) {
        entries.set(organ, { ...entry, status: OrganReviewStatusEnum.IN_PROGRESS });
      }
    });

    this.broadcast(studyInstanceUid, seriesInstanceUid);

    try {
      await submitOrganReviews(seriesInstanceUid, submission);
    } catch (error) {
      // Released BEFORE the rollback, so that every mutation of the latch is followed by a
      // broadcast rather than preceded by the last one.
      this.submitting.delete(seriesInstanceUid);

      if (this.isStale(seriesInstanceUid, generation)) {
        this.broadcast(studyInstanceUid, seriesInstanceUid);
      } else {
        this.restore(studyInstanceUid, seriesInstanceUid, snapshot, submitted);
      }

      this.report(studyInstanceUid, seriesInstanceUid, 'submitting the organ reviews', error);
      throw error;
    }

    if (this.isStale(seriesInstanceUid, generation)) {
      this.submitting.delete(seriesInstanceUid);
      return;
    }

    this.bumpGeneration(seriesInstanceUid);

    await this.refresh(studyInstanceUid, seriesInstanceUid);

    this.submitting.delete(seriesInstanceUid);
    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  public onModeExit(): void {
    this.generations.forEach((_, seriesInstanceUid) => this.bumpGeneration(seriesInstanceUid));

    this.studies.clear();
    this.submitting.clear();
    this.failedReads.clear();
    this.refreshesInFlight.clear();
  }

  private entriesOf(studyInstanceUid: string, seriesInstanceUid: string): SeriesEntries {
    return this.studies.get(studyInstanceUid)?.get(seriesInstanceUid) ?? NO_ENTRIES;
  }

  private mutableEntriesOf(studyInstanceUid: string, seriesInstanceUid: string): SeriesEntries {
    let study = this.studies.get(studyInstanceUid);

    if (!study) {
      study = new Map();
      this.studies.set(studyInstanceUid, study);
    }

    let entries = study.get(seriesInstanceUid);

    if (!entries) {
      entries = new Map();
      study.set(seriesInstanceUid, entries);
    }

    return entries;
  }

  private merge(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    reviews: OrganReview[]
  ): void {
    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);
    const previous = new Map(entries);
    const isSubmitting = this.submitting.has(seriesInstanceUid);
    const seen = new Set<OrganEnum>();

    reviews.forEach(({ reviewId, organ, status, quality }) => {
      seen.add(organ);
      const local = previous.get(organ);

      const keepsLocalQuality =
        local?.isLocalQuality === true &&
        local.reviewId === reviewId &&
        status !== OrganReviewStatusEnum.COMPLETED;

      const holdsInProgress =
        isSubmitting &&
        local?.reviewId === reviewId &&
        local.status === OrganReviewStatusEnum.IN_PROGRESS &&
        status !== OrganReviewStatusEnum.COMPLETED &&
        status !== OrganReviewStatusEnum.FAILED;

      entries.set(organ, {
        reviewId,
        status: holdsInProgress ? OrganReviewStatusEnum.IN_PROGRESS : status,
        quality: keepsLocalQuality ? local.quality : quality,
        isLocalQuality: keepsLocalQuality,
        isOrphaned: false,
      });
    });

    previous.forEach((entry, organ) => {
      if (seen.has(organ)) {
        return;
      }

      if (!entry.isLocalQuality) {
        entries.delete(organ);
        return;
      }

      entries.set(organ, { ...entry, status: undefined, isOrphaned: true });
    });

    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  private restore(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    snapshot: SeriesEntries,
    submitted: Set<string>
  ): void {
    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);

    snapshot.forEach((entry, organ) => {
      if (submitted.has(entry.reviewId)) {
        entries.set(organ, entry);
      }
    });

    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  private broadcast(studyInstanceUid: string, seriesInstanceUid: string): void {
    this.emit(EVENTS.ORGAN_REVIEWS_CHANGED, { studyInstanceUid, seriesInstanceUid });
  }

  private ensureGeneration(seriesInstanceUid: string): void {
    if (!this.generations.has(seriesInstanceUid)) {
      this.generations.set(seriesInstanceUid, 0);
    }
  }

  private generationOf(seriesInstanceUid: string): number {
    return this.generations.get(seriesInstanceUid) ?? 0;
  }

  private bumpGeneration(seriesInstanceUid: string): void {
    this.generations.set(seriesInstanceUid, this.generationOf(seriesInstanceUid) + 1);
  }

  private isStale(seriesInstanceUid: string, generation: number): boolean {
    return this.generationOf(seriesInstanceUid) !== generation;
  }

  private report(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    action: string,
    error: unknown
  ): void {
    console.error(
      `[sycai] ${action} failed ${describeError(error)}; the panel keeps its last state`
    );

    if (error instanceof UnauthorizedException) {
      this.emit(EVENTS.SESSION_LOST, { studyInstanceUid, seriesInstanceUid });
    }
  }
}

export interface OrganReviewService {
  subscribe: (<E extends keyof OrganReviewEventPayloads>(
    eventName: E,
    callback: (data: OrganReviewEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}
